<template>
    <VSelect
        ref="select"
        v-model="users"
        class="v-select--primary v-select--visible v-select--no-option-padding"
        :disabled="disabled"
        :filterable="false"
        label="id"
        :multiple="multiple"
        :options="options"
        :placeholder="t('add-user')"
        @open="fetchOptionsIfNotExists"
        @search="fetchOptions"
    >
        <template v-slot:no-options>{{ t('no-options') }} </template>

        <template v-slot:option="option">
            <div
                class="user-selector__select-option"
                :class="{
                    'user-selector__select-option--selected': contains(option),
                }"
            >
                <VUserCard tiny :user="option"></VUserCard>
            </div>
        </template>

        <template v-slot:selected-option="option">
            <VUserCard tiny :user="option"></VUserCard>
        </template>
    </VSelect>
</template>

<script lang="ts">
// Svg

// Components
import VSelect from './VSelect.vue';
import VUserCard from './VUserCard.vue';

// Other
import { debounce } from 'debounce';
import IUser from '@/core/Models/IUser';
import UserService from '@/core/Services/UserService';
import { defineComponent, markRaw } from 'vue';
import { PropType } from 'vue';
import { useI18n } from 'vue-i18n';

let initialOptions: IUser[] | null = null;

type DebounceFunction = ((search: string) => void) & { clear(): void } & { flush(): void };

export default defineComponent({
    components: {
        VSelect,
        VUserCard,
    },

    props: {
        modelValue: { type: Array as PropType<IUser[]>, default: () => [] },
        multiple: { type: Boolean, default: false },
        editable: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        autofocus: { type: Boolean, default: false },
    },

    setup() {
        const { t } = useI18n();

        return { t };
    },

    data: () => ({
        options: [] as IUser[],
        fetchOptionsDebounce: null as DebounceFunction | null,
    }),

    computed: {
        users: {
            get(): IUser[] {
                return this.modelValue;
            },

            set(value: IUser[]) {
                this.$emit('input', value ?? []);
                this.$emit('update:modelValue', value ?? []);
            },
        },
    },

    methods: {
        contains(user: IUser): boolean {
            return this.users.findIndex((item) => item.id === user.id) !== -1;
        },

        fetchOptionsIfNotExists() {
            if (!this.users.length) {
                this.fetchUsersAsync();
            }
        },

        fetchOptions(search: string, loading: (isLoading: boolean) => void) {
            if (!this.fetchOptionsDebounce) {
                this.fetchOptionsDebounce = debounce(async (search: string) => {
                    loading(true);
                    try {
                        await this.fetchUsersAsync(search);
                        this.fetchOptionsDebounce?.flush();
                        this.fetchOptionsDebounce = null;
                    } finally {
                        loading(false);
                    }
                }, 500); // 0.5s
            }

            this.fetchOptionsDebounce(search);
        },

        async fetchUsersAsync(search = '') {
            if (!search && initialOptions) {
                this.options = initialOptions;
                return;
            }

            const users = await UserService.query({
                page: 1,
                perPage: 10,
                search,
            });

            this.options = users.filter((user) => user.enabled).map((user) => markRaw(user));

            if (!initialOptions) {
                initialOptions = this.options;
            }
        },
    },

    mounted() {
        if (this.autofocus) {
            ((this.$el as HTMLDivElement | null)?.querySelector('.vs__search') as HTMLInputElement | null)?.focus();
        }
    },
});
</script>

<style lang="scss">
.user-selector-simple {
    width: 100%;
}
</style>
